import { useState, useRef, useEffect } from 'react';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { InstallModal } from './InstallModal';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles, Theme } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import GetAppIcon from '@material-ui/icons/GetApp';
import BuildIcon from '@material-ui/icons/Build';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import OfflineBoltIcon from '@material-ui/icons/OfflineBolt';
import { MarketplaceItem } from './types';
import { TYPE_META, isComingSoonType, isWindsurfSupportedType } from './typeMetadata';
import { TypeIcon } from './TypeIcon';
import { useFavorites, favKey } from './FavoritesContext';
import { useMarketplaceFetchApi } from './marketplaceFetch';

const MATURITY_STYLE: Record<string, { bg: string; color: string }> = {
  production: { bg: '#E8F5E9', color: '#2E7D32' },
  beta: { bg: '#FFF3E0', color: '#E65100' },
  experimental: { bg: '#E3F2FD', color: '#1565C0' },
};

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    background: theme.palette.background.paper,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.14s, box-shadow 0.14s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] },
  },
  accentStrip: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    borderRadius: '14px 0 0 14px',
  },
  cardContent: { flexGrow: 1, padding: theme.spacing(2, 2, 2, 2.5) },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(1),
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  typeAvatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '0.85rem',
    flexShrink: 0,
    marginTop: 2,
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '0.93rem',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  authorChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    marginTop: 4,
    padding: '1px 8px 1px 4px',
    borderRadius: 10,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  authorIcon: {
    fontSize: '0.78rem',
    opacity: 0.6,
    flexShrink: 0,
  },
  authorText: {
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    marginTop: 3,
  },

  cardDesc: {
    fontSize: '0.79rem',
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginBottom: theme.spacing(1),
    minHeight: '3.1em',
  },
  typeBadge: {
    height: 18,
    fontSize: '0.67rem',
    fontWeight: 700,
    flexShrink: 0,
    marginTop:'8px',
    '& .MuiChip-label': {
      lineHeight: '18px',
    },
  },
  maturityBadge: {
    height: 18,
    fontSize: '0.67rem',
    fontWeight: 700,
    textTransform: 'capitalize',
  },
  maintenanceBadge: {
    height: 18,
    fontSize: '0.67rem',
    fontWeight: 700,
  },
  cardMaintenance: {
    opacity: 0.72,
    position: 'relative' as const,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'rgba(158, 158, 158, 0.06)',
      pointerEvents: 'none',
      borderRadius: 14,
    },
  },
  keywordsRow: { display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 'auto' },
  keyword: { height: 19, fontSize: '0.66rem', fontWeight: 500 },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    padding: theme.spacing(0.75, 1.5, 1.25),
    gap: theme.spacing(0.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8F9FA',
  },
  downloadBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.74rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  reactionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '3px 6px',
    borderRadius: 12,
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    transition: 'opacity 0.12s',
    '&:hover': { opacity: 0.7 },
  },
  btnAction: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 20,
    fontSize: '0.74rem',
    padding: theme.spacing(0.35, 1.5),
    color: '#fff',
    boxShadow: 'none',
    '&:hover': {
      filter: 'brightness(0.85)',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: '#fff',
      backgroundColor: '#9EA4AB',
    },
  },
  iconBtn: {
    color: theme.palette.text.secondary,
    padding: 5,
  },
}));

interface MarketplaceCardProps {
  item: MarketplaceItem;
}

export function MarketplaceCard({ item }: MarketplaceCardProps) {
  const classes = useStyles();
  const navigate = useNavigate();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useMarketplaceFetchApi();

  const [installMenuAnchor, setInstallMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [backendBaseUrl, setBackendBaseUrl] = useState<string | undefined>();
  const [localUp, setLocalUp] = useState(item.upCount ?? 0);
  const [localDown, setLocalDown] = useState(item.downCount ?? 0);
  const installBtnRef = useRef<HTMLButtonElement>(null);
  const identityApi = useApi(identityApiRef);
  const { favorites, toggleFavorite } = useFavorites();
  const isFavorited = favorites.has(favKey(item.name, item.type));

  useEffect(() => {
    discoveryApi
      .getBaseUrl('marketplace')
      .then(setBackendBaseUrl)
      .catch(() => {});
  }, [discoveryApi]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('marketplace');
        const { userEntityRef } = await identityApi.getBackstageIdentity();
        const resp = await fetchApi.fetch(
          `${base}/reactions/${encodeURIComponent(item.name)}?user=${encodeURIComponent(userEntityRef)}&type=${encodeURIComponent(item.type)}`,
        );
        if (resp.ok && !cancelled) {
          const data = await resp.json();
          setLocalUp(data.upCount ?? 0);
          setLocalDown(data.downCount ?? 0);
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi, identityApi, item.name, item.type]);

  const handleReaction = async (type: 'up' | 'down', e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const base = await discoveryApi.getBaseUrl('marketplace');
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const resp = await fetchApi.fetch(
        `${base}/reactions/${encodeURIComponent(item.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userRef: userEntityRef, type, artifactType: item.type }),
        },
      );
      if (resp.ok) {
        const data = await resp.json();
        setLocalUp(data.upCount);
        setLocalDown(data.downCount);
      }
    } catch {
      // ignore
    }
  };

  const meta = TYPE_META[item.type] ?? {
    label: item.type,
    iconName: 'widgets' as const,
    color: '#666666',
    btnColor: '#666666',
    gradient: 'linear-gradient(135deg, #666666, #999999)',
  };
  const isComingSoon = isComingSoonType(item.type);
  const isMaintenance = item.status === 'maintenance';

  const canInstall = !!(
    item.rawBaseUrl &&
    (item.agentMdFile || item.contentFile)
  ) && !isMaintenance;
  const maturityStyle = item.maturity
    ? MATURITY_STYLE[item.maturity.toLowerCase()]
    : undefined;

  const download = async () => {
    setDownloadError(null);

    if (!item.rawBaseUrl || !item.contentFile) {
      // For types without download, just use GitHub link
      if (item.repoUrl) {
        window.open(item.repoUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    try {
      const baseUrl = await discoveryApi.getBaseUrl('marketplace');
      const params = new URLSearchParams({
        rawBaseUrl: item.rawBaseUrl,
        contentFile: item.contentFile,
      });
      const resp = await fetchApi.fetch(`${baseUrl}/zip?${params}`);
      if (!resp.ok) {
        throw new Error(`Server error: ${resp.status}`);
      }
      const blob = await resp.blob();
      const folderName = item.rawBaseUrl.split('/').pop() ?? item.name;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${folderName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (error: any) {
      setDownloadError(
        `Download failed — try the GitHub link: ${error.message}`,
      );
    }
  };

  const handleCardClick = () => {
    sessionStorage.setItem('mp-scroll-y', `${window.scrollY}:${Date.now()}`);
    navigate(
      `/ai-marketplace?type=${encodeURIComponent(item.type)}&id=${encodeURIComponent(item.id)}`,
    );
  };

  const handlePrimaryAction = () => {
    if (isComingSoon) {
      return;
    }
    if (item.type === 'agent') {
      download();
      return;
    }
    if (
      item.type === 'model' ||
      item.type === 'mcp-server' ||
      item.type === 'template' ||
      item.type === 'playbook'
    ) {
      handleCardClick();
      return;
    }
    download();
  };

  const primaryActionLabel = isComingSoon
    ? 'Coming Soon'
    : item.type === 'model'
      ? 'View Details'
      : item.type === 'mcp-server'
        ? 'View Details'
        : item.type === 'template'
          ? 'View Details'
          : item.type === 'playbook'
            ? 'View Details'
            : 'Download';

  return (
    <>
      <Card className={`${classes.card}${isMaintenance ? ` ${classes.cardMaintenance}` : ''}`} elevation={0}>
        <Box
          className={classes.accentStrip}
          style={{ background: isMaintenance ? '#9E9E9E' : meta.gradient }}
        />
        <CardContent
          className={classes.cardContent}
          style={{ cursor: 'pointer' }}
          onClick={handleCardClick}
        >
          <Box className={classes.cardHeader}>
            <Box className={classes.titleWrap}>
              <Box
                className={classes.typeAvatar}
                style={{ background: meta.gradient }}
              >
                <TypeIcon
                  iconName={meta.iconName}
                  style={{ color: '#fff', fontSize: '0.95rem' }}
                  fontSize="inherit"
                />
              </Box>
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Typography
                  className={classes.cardTitle}
                  title={item.displayName || item.name}
                >
                  {item.displayName}
                </Typography>
                {item.author && (
                  <Box
                    className={classes.authorChip}
                    style={{ border: `1px solid ${meta.color}` }}
                  >
                    <PersonOutlineIcon
                      className={classes.authorIcon}
                      color="action"
                    />
                    <Typography
                      className={classes.authorText}
                      color="textSecondary"
                    >
                      {item.author.replace(/_verint$/i, '').replace(/[-_.]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box className={classes.badges}>
              <Tooltip title={isFavorited ? 'Remove from favorites' : 'Add to your favorites'}>
                <IconButton
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    toggleFavorite(item.name, item.type);
                  }}
                  style={{ padding: 2, marginRight: 2 }}
                  aria-label={isFavorited ? `Remove ${item.name} from favorites` : `Add ${item.name} to favorites`}
                >
                  {isFavorited ? (
                    <BookmarkIcon style={{ fontSize: 18, color: '#FFB020' }} />
                  ) : (
                    <BookmarkBorderIcon style={{ fontSize: 18, color: '#9e9e9e' }} />
                  )}
                </IconButton>
              </Tooltip>
              <Chip
                label={meta.label}
                size="small"
                className={classes.typeBadge}
                style={{
                  backgroundColor: `${meta.color}20`,
                  color: meta.color,
                }}
              />

              {item.maturity && maturityStyle && (
                <Chip
                  label={item.maturity}
                  size="small"
                  className={classes.maturityBadge}
                  style={{
                    backgroundColor: maturityStyle.bg,
                    color: maturityStyle.color,
                  }}
                />
              )}
              {item.tokenEfficient === true && (
                <Tooltip title={`Token Efficiency Score: ${item.tokenScore ?? '—'}/100`}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(9,167,97,0.12)',
                      border: '1px solid rgba(9,167,97,0.28)',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                      verticalAlign: 'middle',
                    }}
                  >
                    <OfflineBoltIcon style={{ fontSize: 12, color: '#09A761' }} />
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Typography className={classes.cardDesc}>
            {item.description || 'No description available.'}
          </Typography>
            {item.keywords.length > 0 && (
            <Box className={classes.keywordsRow}>
              {item.keywords.slice(0, 4).map(kw => (
                <Chip
                  key={kw}
                  label={kw}
                  size="small"
                  variant="outlined"
                  className={classes.keyword}
                />
              ))}
              {item.keywords.length > 4 && (
                <Tooltip title={item.keywords.slice(4).join(', ')}>
                  <Chip
                    label={`+${item.keywords.length - 4}`}
                    size="small"
                    variant="outlined"
                    className={classes.keyword}
                  />
                </Tooltip>
              )}
            </Box>
          )}
        </CardContent>
        <Box className={classes.cardFooter}>
          {isComingSoon && (
            <Button
              size="small"
              variant="contained"
              className={classes.btnAction}
              startIcon={<GetAppIcon fontSize="small" />}
              onClick={handlePrimaryAction}
              disabled
            >
              Coming Soon
            </Button>
          )}
          {!isComingSoon && isMaintenance && (
            <Tooltip title="This artifact is under maintenance and may not function correctly">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  className={classes.btnAction}
                  startIcon={<BuildIcon style={{ fontSize: 14 }} />}
                  disabled
                >
                  Maintenance
                </Button>
              </span>
            </Tooltip>
          )}
          {!isComingSoon && canInstall && (
            <ButtonGroup
              size="small"
              variant="contained"
              color="primary"
              style={{
                boxShadow: 'none',
                borderRadius: 20,
                overflow: 'hidden',
              }}
            >
              <Button
                className={classes.btnAction}
                style={{ backgroundColor: meta.btnColor }}
                onClick={e => {
                  e.stopPropagation();
                  setInstallModalOpen(true);
                }}
                startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
              >
                Download
              </Button>
              <Button
                ref={installBtnRef}
                size="small"
                className={classes.btnAction}
                style={{
                  minWidth: 26,
                  padding: '0 4px',
                  backgroundColor: meta.btnColor,
                }}
                onClick={e => {
                  e.stopPropagation();
                  setInstallMenuAnchor(e.currentTarget);
                }}
                aria-label="Select IDE"
              >
                <ArrowDropDownIcon fontSize="small" />
              </Button>
            </ButtonGroup>
          )}
          {!isComingSoon && canInstall && (
            <Menu
              anchorEl={installMenuAnchor}
              open={Boolean(installMenuAnchor)}
              onClose={() => setInstallMenuAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setInstallModalOpen(true);
                  setInstallMenuAnchor(null);
                }}
              >
                VS Code
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setInstallModalOpen(true);
                  setInstallMenuAnchor(null);
                }}
              >
                VS Code Insiders
              </MenuItem>
              {isWindsurfSupportedType(item.type) && (
                <MenuItem
                  onClick={() => {
                    setInstallModalOpen(true);
                    setInstallMenuAnchor(null);
                  }}
                >
                  Windsurf
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  download();
                  setInstallMenuAnchor(null);
                }}
              >
                IntelliJ IDEA
                <span
                  style={{ fontSize: '0.7rem', opacity: 0.6, marginLeft: 6 }}
                >
                  downloads zip
                </span>
              </MenuItem>
            </Menu>
          )}
          {item.type === 'agent' && !canInstall && !isMaintenance && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              className={classes.btnAction}
              style={{
                backgroundColor: 'transparent',
                color: meta.btnColor,
                border: `1px solid ${meta.btnColor}`,
              }}
              onClick={download}
            >
              View
            </Button>
          )}
          {!isComingSoon && item.type !== 'agent' && !canInstall && !isMaintenance && (
            <Tooltip title="Download / view on GitHub">
              <Button
                size="small"
                variant="contained"
                color="primary"
                className={classes.btnAction}
                style={{ backgroundColor: meta.btnColor }}
                startIcon={<GetAppIcon fontSize="small" />}
                onClick={handlePrimaryAction}
              >
                {primaryActionLabel}
              </Button>
            </Tooltip>
          )}
          {(item.downloadCount ?? 0) > 0 && (
            <Box className={classes.downloadBadge}>
              <GetAppIcon style={{ fontSize: 12, opacity: 0.6, position: 'relative', top: 1 }} />
              {item.downloadCount}
            </Box>
          )}
          <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconButton
              size="small"
              className={classes.reactionBtn}
              onClick={(e: React.MouseEvent) => handleReaction('up', e)}
              style={{ color: '#1565C0' }}
              aria-label={`Like ${item.name}${localUp > 0 ? `, ${localUp} likes` : ''}`}
            >
              <ThumbUpIcon style={{ fontSize: 14, marginRight: 4 }} />
              {localUp > 0 && <span>{localUp}</span>}
            </IconButton>
            <IconButton
              size="small"
              className={classes.reactionBtn}
              onClick={(e: React.MouseEvent) => handleReaction('down', e)}
              style={{ color: '#C62828', display: 'inline-flex', alignItems: 'center', gap: 8 }}
              aria-label={`Dislike ${item.name}${localDown > 0 ? `, ${localDown} dislikes` : ''}`}
            >
              <ThumbDownIcon style={{ fontSize: 14, marginRight: 4 }} />
              {localDown > 0 && <span>{localDown}</span>}
            </IconButton>
            {item.repoUrl && (
              <Tooltip title="Open on GitHub">
                <IconButton
                  size="small"
                  className={classes.iconBtn}
                  href={item.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Card>
      <InstallModal
        open={installModalOpen}
        onClose={() => setInstallModalOpen(false)}
        item={item}
        backendBaseUrl={backendBaseUrl}
      />
      <Snackbar
        open={!!downloadError}
        autoHideDuration={6000}
        onClose={() => setDownloadError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setDownloadError(null)}
          severity="error"
          style={{ width: '100%' }}
        >
          {downloadError}
        </Alert>
      </Snackbar>
    </>
  );
}
